import { BookingDetails, BookingStatus } from 'hotdesk_typescript';
import {
    BookingStatusMap,
    BookingStatusTextColor,
    UpcommingAndOnGoingStatus
} from '@/hotdesk-ui/constants/booking.constant';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { resolveBookingInstant } from './bookingDate';
import { getUserLocale } from '../lib/language.service';
import { BookingService } from '../services/booking.service';
import { ApiConfigration } from '../common-classes/api-configuration';
import { format } from 'date-fns';

dayjs.extend(duration);

const bookingStatusMap: Record<string, string> = {
    ...BookingStatusMap,
    [BookingStatus.PENDING_CONFIRMATION_BY_HOST]:
        'booking.displayStatus.requested'
};

type TimeDiff = {
    days: number;
    hours: number;
    months: number;
    minutes: number;
    weeks: number;
    years: number;
};

export const hasRemaingTime = (
    date?: Date | string,
    timezone?: string | null
): boolean => {
    const {
        days = 0,
        months = 0,
        hours = 0,
        minutes = 0
    } = getRemainingTime(date, undefined, timezone);
    return days > 0 || months > 0 || hours > 0 || minutes > 0;
};

export const getRemainingTime = (
    date?: Date | string,
    currentDate?: Date,
    timezone?: string | null
): TimeDiff => {
    // resolveBookingInstant, not parseBookingUtcInstant: an offset-less stored
    // value is the listing's wall clock, not UTC (see bookingDate.ts). Reading
    // a day booking's `22:00` close as UTC kept a +3 listing's booking
    // "running" for three hours after it actually ended — and this feeds
    // hasRemaingTime, which drives upcoming-vs-history placement, so the
    // boundary cases flipped tab.
    const target = date ? resolveBookingInstant(date, timezone) : dayjs(date);
    const now = currentDate ? dayjs(currentDate) : dayjs();

    const diffMs = target.diff(now);
    const duration = dayjs.duration(diffMs);

    return {
        days: Math.round(duration.asDays()),
        hours: Math.round(duration.asHours()),
        months: Math.round(duration.asMonths()),
        minutes: Math.round(duration.asMinutes()),
        weeks: Math.round(duration.asWeeks()),
        years: Math.round(duration.asYears())
    };
};
export const getUnitText = (value: number, type: string): string => {
    const normalized = (type || 'day').toLowerCase();
    let unitText = value > 1 ? 'booking.days' : 'booking.day';
    switch (true) {
        case normalized === 'month':
            unitText = value > 1 ? 'booking.months' : 'booking.month';
            break;
        case normalized === 'hour':
            unitText = value > 1 ? 'booking.hours' : 'booking.hour';
            break;
        case normalized === 'year':
        case normalized === 'annual':
            unitText = value > 1 ? 'booking.years' : 'booking.year';
            break;
        case normalized === 'minute':
            unitText = value > 1 ? 'booking.minutes' : 'booking.minute';
            break;
        case normalized === 'month-late':
            unitText = value > 1 ? 'booking.monthsLate' : 'booking.monthLate';
            break;
        case normalized === 'hour-late':
            unitText = value > 1 ? 'booking.hoursLate' : 'booking.hourLate';
            break;
        case normalized === 'annual-late':
        case normalized === 'year-late':
            unitText = value > 1 ? 'booking.yearsLate' : 'booking.yearLate';
            break;
        case normalized === 'minute-late':
            unitText = value > 1 ? 'booking.minutesLate' : 'booking.minuteLate';
            break;
        case normalized === 'day-late':
            unitText = value > 1 ? 'booking.daysLate' : 'booking.dayLate';
            break;
    }
    return unitText;
};

export const getDisplayBookingStatus = (
    status: BookingStatus,
    bookingDetails: BookingDetails,
    t: (key: string) => string
): {
    status: string;
    statusTextColor: string;
} => {
    // Whether the listing actually requires a check-in — drives the
    // "No Show" / late-check-in derivations below. Was hardcoded `true`,
    // which verdicted every past booking without a `userCheckedIn` as
    // "No Show", even on listings with no check-in flow at all (all
    // provider/IWG bookings rest in PENDING_CAPTURE_MONEY and never get
    // a check-in, so every past one displayed "No Show"). Default false:
    // when the BE doesn't say check-in is required, a past booking reads
    // "Ended", not a verdict the BE never issued.
    const isCheckinEnabled = bookingDetails?.isCheckinRequired ?? false;
    let validTill: number | undefined = undefined;
    let validUnit = '';
    let isEndSoon = false;
    let isLateChecking = false;
    const {
        days = 0,
        months = 0,
        hours = 0,
        minutes = 0
    } = getRemainingTime(
        bookingDetails.endDateTime,
        undefined,
        bookingDetails.timezone
    );
    switch (true) {
        case months >= 1:
            validTill = Math.floor(months);
            validUnit = getUnitText(validTill, 'month');
            break;
        case days >= 1:
            validTill = Math.floor(days);
            validUnit = getUnitText(validTill, 'day');
            break;
        case hours >= 1:
            validTill = Math.floor(hours);
            validUnit = getUnitText(validTill, 'hour');
            break;
        case minutes >= 1:
            validTill = Math.floor(minutes);
            validUnit = getUnitText(validTill, 'minute');
            break;
    }
    if (Math.floor(hours) <= 1) {
        isEndSoon = true;
    }
    if (validTill === undefined && !!isCheckinEnabled) {
        switch (true) {
            case months <= -1:
                validTill = Math.floor(-1 * months);
                validUnit = getUnitText(validTill, 'month-late');
                break;
            case days <= -1:
                validTill = Math.floor(-1 * days);
                validUnit = getUnitText(validTill, 'day-late');
                break;
            case hours <= -1:
                validTill = Math.floor(-1 * hours);
                validUnit = getUnitText(validTill, 'hour-late');
                break;
            case minutes <= -1:
                validTill = Math.floor(-1 * minutes);
                validUnit = getUnitText(validTill, 'minute-late');
                break;
        }
        if (!!validTill) {
            isLateChecking = true;
        }
    }

    let desplayStatus = {
        status: bookingStatusMap[status],
        statusTextColor: BookingStatusTextColor[status]
    };

    if (!!validUnit) validUnit = t(`${validUnit}`);
    if (
        status === BookingStatus.PENDING_CONFIRMATION_BY_HOST &&
        !hasRemaingTime(bookingDetails?.expirationThreshold)
    ) {
        desplayStatus = {
            status: 'booking.displayStatus.expired',
            statusTextColor: 'text-inactive'
        };
    } else if (
        (UpcommingAndOnGoingStatus.indexOf(status) !== -1 &&
            !bookingDetails.userCheckedIn &&
            hasRemaingTime(
            bookingDetails?.endDateTime,
            bookingDetails?.timezone
        ) &&
            isCheckinEnabled) ||
        (UpcommingAndOnGoingStatus.indexOf(status) !== -1 &&
            hasRemaingTime(
            bookingDetails?.endDateTime,
            bookingDetails?.timezone
        ) &&
            !isCheckinEnabled)
    ) {
        desplayStatus = {
            status:
                (isLateChecking || isEndSoon) && !!validTill
                    ? validTill + ' ' + t(`${validUnit}`)
                    : bookingStatusMap[status],
            statusTextColor:
                isLateChecking && !!validTill
                    ? 'text-danger'
                    : isEndSoon && !!validTill
                    ? 'text-orange'
                    : 'text-success'
        };
    } else if (
        // Checked in or not, an accepted booking whose end has passed is
        // "Ended". No Show is not inferred here from missing check-ins: it is
        // status 12, set by the host or an admin, and bookingStatusMap labels it.
        UpcommingAndOnGoingStatus.indexOf(status) !== -1 &&
        !hasRemaingTime(
            bookingDetails?.endDateTime,
            bookingDetails?.timezone
        )
    ) {
        desplayStatus = {
            status: 'booking.displayStatus.ended',
            statusTextColor: 'text-orange'
        };
    }
    return desplayStatus;
};
/**
 * `timezone` is `booking.details.timezone`, and it must be threaded all the way
 * down to `getRemainingTime` — that is where an offset-less stored value gets
 * read as the listing's wall clock rather than as UTC. Without it this chain
 * silently falls back to the UTC reading, so a +3 listing's booking stays
 * "running" for three more hours and the boundary cases land in the wrong tab.
 * `getDisplayBookingStatus` already passes it; this is the parallel chain that
 * drives the remaining-time badge.
 */
export const getSelectedTab = async (
    type: string,
    endDate?: Date | string,
    timezone?: string | null
) => {
    let getRemainingTime = {
        isLateChecking: false,
        validUnit: '',
        validTill: 0,
        isEndSoon: false
    };
    switch (true) {
        case type === 'pending':
            break;
        case type === 'upcoming':
            getRemainingTime = await setRemaingTime(endDate, timezone).then();
            break;
        case type === 'ongoing':
            getRemainingTime = await setRemaingTime(endDate, timezone).then();
            break;
        case type === 'history':
            break;
    }
    return getRemainingTime;
};

export const headerBgMapWithUpcoming = (
    data:
        | {
              isLateChecking: boolean;
              validUnit: string;
              validTill: number;
              isEndSoon: boolean;
          }
        | undefined,
    guestUser: number | undefined
) => {
    let bgColor = '';
    if (!!guestUser) bgColor = 'bg-orange';
    else if (!!data && data.isLateChecking && !guestUser)
        bgColor = 'hot-pinkish';
    else if (!!data && !data.isLateChecking && !guestUser)
        bgColor = 'bg-hot-success';
    else if (!!data && data.isLateChecking) bgColor = 'hot-pinkish';
    else if (!!data && !data.isEndSoon) bgColor = 'bg-hot-success';
    else if (!!data && data.isEndSoon) bgColor = 'bg-orange';
    return bgColor;
};
export const headerbulletsMapWithUpcoming = (
    data:
        | {
              isLateChecking: boolean;
              validUnit: string;
              validTill: number;
              isEndSoon: boolean;
          }
        | undefined,
    guestUser: number | undefined
) => {
    let bgColor = '';
    if (!!guestUser) bgColor = 'bg-circle-gold';
    else if (!!data && data.isLateChecking && !guestUser)
        bgColor = 'bg-circle-hot-red';
    else if (!!data && !data.isLateChecking && !guestUser)
        bgColor = 'bg-circle-forest-green';
    else if (!!data && data.isLateChecking) bgColor = 'hot-pinkish';
    else if (!!data && !data.isEndSoon) bgColor = 'bg-hot-success';
    else if (!!data && data.isEndSoon) bgColor = 'bg-orange';
    return bgColor;
};
export const headerbulletsMapWithHistory = (status: BookingStatus) => {
    let bulletCircleColor = '';
    switch (true) {
        case status === 1:
            bulletCircleColor = 'bg-circle-forest-green';
            break;
        case status === 2:
            bulletCircleColor = 'bg-circle-forest-green';
            break;
        case status === 3:
            bulletCircleColor = 'bg-circle-forest-green';
            break;
        case status === 5:
            bulletCircleColor = 'bg-circle-hot-red';
            break;
        case status === 6:
            bulletCircleColor = 'bg-circle-hot-red';
            break;
        case status === 9:
            bulletCircleColor = 'bg-circle-forest-green';
            break;
        case status === 10:
            bulletCircleColor = 'bg-circle-muted';
            break;
        case status === 11:
            bulletCircleColor = 'bg-circle-muted';
            break;
    }
    return bulletCircleColor;
};

export const setRemaingTime = async (
    endDate?: Date | string,
    timezone?: string | null
) => {
    const languageCode = getUserLocale();
    const getBookingService = async (
        languageCode: string
    ): Promise<BookingService> => {
        const apiConfig = await new ApiConfigration().getApiConfig(
            languageCode
        );
        return new BookingService(apiConfig);
    };
    const service = await getBookingService(await languageCode);
    let { isLateChecking, validUnit, validTill, isEndSoon } =
        service.checkDateTimeStatus(endDate, true, timezone);
    isLateChecking = isLateChecking;
    validUnit = validUnit;
    validTill = validTill as number;
    isEndSoon = isEndSoon;
    return {
        isLateChecking: isLateChecking,
        validUnit: validUnit,
        validTill: validTill,
        isEndSoon: isEndSoon
    };
};
/**
 * "Now" as a naive `yyyy-MM-ddTHH:mm:ss` string in **UTC**, for the booking
 * list's date filters.
 *
 * The backend stores booking start/end as timezone-aware UTC and parses a
 * naive string as UTC (`TIME_ZONE = "UTC"`). This used to format in local
 * time, so a client at UTC+3 sent a "now" three hours in the future and the
 * Upcoming/Ongoing/History boundaries were skewed by the caller's offset --
 * and the caller is the Node server on first paint but the browser on
 * "Show More", so the two could even disagree with each other.
 *
 * The separator is `T`, matching `toApiStartDateTimeGte`/`Lte`. Django parses
 * either, so the wire format itself is a free choice -- but this value is also
 * *compared* against those two as a plain string (the Ongoing branch's
 * `userStartLte < now`), and a space sorts before `T`. With the old `" "`
 * separator a same-day `…T00:00:00` compared as *greater* than a later
 * `… 14:05:00`, inverting the comparison on exactly the same-day case it
 * exists to decide. Keep both sides on one separator, or make those
 * comparisons parse instead.
 */
export const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().slice(0, 19);
};
